// Subscription Service - Manage user subscription tiers and feature access
import { doc, updateDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

export interface Subscription {
  tier: 'free' | 'premium' | 'trial' | 'lifetime';
  status: 'active' | 'inactive' | 'trial' | 'expired' | 'lifetime';
  trialEnd?: Date;
  startDate: Date;
  endDate?: Date;
  autoRenew: boolean;
  features: string[];
  limitations: SubscriptionLimitations;
  // Lifetime access fields
  lifetimeAccessNumber?: number;   // Founding Member #XX (1–100)
  lifetimeGrantedAt?: Date;
  lifetimeGrantedBy?: string;      // 'system' | adminUserId
}

export interface SubscriptionLimitations {
  maxConnections: number;
  maxPosts: number; // Per maand
  maxStorage: string;
  maxBusinessCards: number;
  canCreateBusinessCards: boolean;
  mastermindAccess: boolean;
  aiFeatures: boolean;
  youtubeApi: boolean;
  calendarApi: boolean;
  videoUpload: boolean;
  linkedinApi: boolean;
  maxVibeScoreAnalyses: number;
  maxMoodChecks: number;
  personalizedCoaching: boolean;
  aiAffirmations: boolean;
  stressMonitoring: boolean;
}

export const FREE_TIER_LIMITATIONS: SubscriptionLimitations = {
  maxConnections: 5,
  maxPosts: 5, // Per maand
  maxStorage: "0MB",
  maxBusinessCards: 1,
  canCreateBusinessCards: false,
  mastermindAccess: false,
  aiFeatures: false, // false = 10 AI gesprekken per maand (zie aiUsageService)
  youtubeApi: false,
  calendarApi: false,
  videoUpload: false,
  linkedinApi: false,
  maxVibeScoreAnalyses: 5, // Per maand
  maxMoodChecks: 1, // Per dag
  personalizedCoaching: false,
  aiAffirmations: false,
  stressMonitoring: false
};

export const PREMIUM_TIER_LIMITATIONS: SubscriptionLimitations = {
  maxConnections: -1, // Unlimited
  maxPosts: -1, // Unlimited
  maxStorage: "10GB",
  maxBusinessCards: -1, // Unlimited
  canCreateBusinessCards: true,
  mastermindAccess: true,
  aiFeatures: true, // true = Unlimited AI gesprekken (zie aiUsageService)
  youtubeApi: true,
  calendarApi: true,
  videoUpload: true,
  linkedinApi: true,
  maxVibeScoreAnalyses: -1, // Unlimited
  maxMoodChecks: -1, // Unlimited
  personalizedCoaching: true,
  aiAffirmations: true,
  stressMonitoring: true
};

export const FREE_TIER: Subscription = {
  tier: 'free',
  status: 'active',
  startDate: new Date(),
  autoRenew: false,
  features: [
    'Community Dashboard toegang',
    'Basis networking (5 connecties/maand)',
    'Training Center (statische content)',
    'Basis VibeScore (lokaal berekend)',
    'Mijn Verhaal - 1 bedrijf kaart bekijken',
    'Basis mindfulness tips (statische content)',
    'Eenvoudige breathing exercise (lokaal)'
  ],
  limitations: FREE_TIER_LIMITATIONS
};

export const PREMIUM_TIER: Subscription = {
  tier: 'premium',
  status: 'active',
  startDate: new Date(),
  autoRenew: true,
  features: [
    'Alles van gratis tier +',
    'Onbeperkte connecties',
    'AI matchmaking',
    'Google Calendar integratie',
    'Video uploads & opnames',
    'Premium training modules',
    'LinkedIn autopost',
    'AI-powered VibeScore analyse',
    'Gepersonaliseerde mindfulness coaching',
    'AI-generated affirmations',
    'Advanced stress level monitoring',
    'Personalized meditation recommendations'
  ],
  limitations: PREMIUM_TIER_LIMITATIONS
};

// Lifetime tier heeft dezelfde mogelijkheden als premium — maar nooit verloopt
export const LIFETIME_TIER: Subscription = {
  tier: 'lifetime',
  status: 'lifetime',
  startDate: new Date(),
  autoRenew: false,   // Geen verlenging nodig — het is lifetime
  features: [
    'Alles van premium tier +',
    'Lifetime toegang — nooit verlengd of geblokkeerd',
    'Founding Member badge op profiel',
    'Prioriteit support',
    'Vroege toegang tot nieuwe features',
  ],
  limitations: PREMIUM_TIER_LIMITATIONS,  // Zelfde mogelijkheden als premium
};

class SubscriptionService {
  /**
   * Get user subscription from Firestore
   */
  async getUserSubscription(userId: string): Promise<Subscription | null> {
    try {
      const userDoc = await getDoc(doc(db, 'userProfiles', userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        return userData.subscription || FREE_TIER;
      }
      return FREE_TIER;
    } catch (error) {
      console.error('Error getting user subscription:', error);
      return FREE_TIER;
    }
  }

  /**
   * Start 7-day premium trial
   */
  async startTrial(userId: string): Promise<Subscription> {
    const trialEnd = new Date();
    trialEnd.setDate(trialEnd.getDate() + 7);
    
    const trialSubscription: Subscription = {
      ...PREMIUM_TIER,
      tier: 'trial',
      status: 'trial',
      trialEnd: trialEnd,
      autoRenew: false
    };

    try {
      await updateDoc(doc(db, 'userProfiles', userId), {
        subscription: trialSubscription,
        updatedAt: serverTimestamp()
      });
      
      console.log('✅ Trial started for user:', userId);
      return trialSubscription;
    } catch (error) {
      console.error('Error starting trial:', error);
      throw error;
    }
  }

  /**
   * Upgrade to premium subscription
   */
  async upgradeToPremium(userId: string): Promise<Subscription> {
    try {
      await updateDoc(doc(db, 'userProfiles', userId), {
        subscription: PREMIUM_TIER,
        updatedAt: serverTimestamp()
      });
      
      console.log('✅ User upgraded to premium:', userId);
      return PREMIUM_TIER;
    } catch (error) {
      console.error('Error upgrading to premium:', error);
      throw error;
    }
  }

  /**
   * Downgrade to free tier
   */
  async downgradeToFree(userId: string): Promise<Subscription> {
    try {
      await updateDoc(doc(db, 'userProfiles', userId), {
        subscription: FREE_TIER,
        updatedAt: serverTimestamp()
      });
      
      console.log('✅ User downgraded to free:', userId);
      return FREE_TIER;
    } catch (error) {
      console.error('Error downgrading to free:', error);
      throw error;
    }
  }

  /**
   * Check if user has lifetime access
   */
  isLifetime(subscription: Subscription): boolean {
    return subscription.tier === 'lifetime' && subscription.status === 'lifetime';
  }

  /**
   * Check if user can access a specific feature
   * Lifetime = altijd premium toegang
   */
  canAccessFeature(subscription: Subscription, feature: string): boolean {
    // Lifetime heeft overal toegang toe
    if (this.isLifetime(subscription)) return true;

    switch (feature) {
      case 'create_business_card':
        return subscription.tier === 'premium' || 
               (subscription.tier === 'free' && subscription.limitations.canCreateBusinessCards);
               
      case 'mastermind_groups':
        return subscription.limitations.mastermindAccess;
        
      case 'ai_matchmaking':
        return subscription.limitations.aiFeatures;
        
      case 'video_upload':
        return subscription.limitations.videoUpload;
        
      case 'youtube_api':
        return subscription.limitations.youtubeApi;
        
      case 'calendar_api':
        return subscription.limitations.calendarApi;
        
      case 'linkedin_api':
        return subscription.limitations.linkedinApi;
        
      case 'ai_vibescore':
        return subscription.limitations.aiFeatures;
        
      case 'personalized_coaching':
        return subscription.limitations.personalizedCoaching;
        
      case 'ai_affirmations':
        return subscription.limitations.aiAffirmations;
        
      case 'stress_monitoring':
        return subscription.limitations.stressMonitoring;
        
      default:
        return true;
    }
  }

  /**
   * Check if trial is still active
   */
  isTrialActive(subscription: Subscription): boolean {
    if (subscription.tier !== 'trial' || !subscription.trialEnd) {
      return false;
    }
    
    return new Date() < subscription.trialEnd;
  }

  /**
   * Get remaining trial days
   */
  getRemainingTrialDays(subscription: Subscription): number {
    if (!this.isTrialActive(subscription) || !subscription.trialEnd) {
      return 0;
    }
    
    const now = new Date();
    const trialEnd = subscription.trialEnd;
    const diffTime = trialEnd.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return Math.max(0, diffDays);
  }
}

export default new SubscriptionService();













